/**
 * NeoCore · Reporte de intereses pagados + retenciones ISR
 *
 * Réplica del reporte del core viejo COBASY ("EJEMPLO REPORTE INTERES NEO.xlsx")
 * pedido por Carlitos. Una fila por cada acreditación de interés y cada
 * descuento de ISR, con filtros de fecha, tipo y asociado.
 *
 * Export Excel con 2 hojas (como el original):
 *   Hoja 1 · Detalle de movimientos
 *   Hoja 2 · Asociados con DPI (para gestiones SAT)
 *
 * RPC: reporte_intereses_isr (migración 0146). Task #154.
 */

import { useEffect, useMemo, useState } from 'react'
import { supabase } from '@/lib/supabase'
import { Funnel, FilePdf, MicrosoftExcelLogo, Printer } from '@phosphor-icons/react'
import { exportarPDF } from '@/lib/exportReport'
import { fetchConfigReporte } from '@/lib/useConfigReporte'
import { hoyGT } from '../lib/fechas'

type Row = {
  fecha: string
  servicio: string
  numero_cuenta: string
  no_asociado: string
  asociado_nombre: string
  dpi: string
  nit: string
  nit_es_cui: boolean
  tipo_movimiento: 'PAGO DE INTERES' | 'DESCUENTO ISR'
  descripcion: string
  credito: number
  debito: number
  total: number
  origen: string
  forma_pago: string
}

const fmt = (n: number) => Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const hoy = () => hoyGT()
const inicioMes = () => hoy().slice(0, 8) + '01'

export function NeoCoreReporteInteresesPage() {
  const [desde, setDesde] = useState(inicioMes())
  const [hasta, setHasta] = useState(hoy())
  const [tipo, setTipo] = useState('')      // '' | 'INTERES' | 'ISR'
  const [search, setSearch] = useState('')
  const [rows, setRows] = useState<Row[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const cargar = async () => {
    setLoading(true); setError(null)
    const { data, error } = await supabase.rpc('reporte_intereses_isr', {
      p_fecha_desde: desde,
      p_fecha_hasta: hasta,
      p_tipo: tipo || null,
      p_asociado_id: null,
      p_search: search || null,
    })
    setLoading(false)
    if (error) { setError(error.message); return }
    setRows((data ?? []) as Row[])
  }

  useEffect(() => { cargar() }, [])

  const totales = useMemo(() => {
    const intereses = rows.filter(r => r.tipo_movimiento === 'PAGO DE INTERES').reduce((s, r) => s + Number(r.credito), 0)
    const isr = rows.filter(r => r.tipo_movimiento === 'DESCUENTO ISR').reduce((s, r) => s + Number(r.debito), 0)
    return { intereses, isr, neto: intereses - isr }
  }, [rows])

  // ── Export Excel · 2 hojas como el reporte COBASY original ──
  const onExcel = async () => {
    const XLSX = await import('xlsx')
    const config = await fetchConfigReporte()

    // Hoja 1 · Detalle
    const encabezado = [
      [config?.razon_social ?? 'NEOCOOP, R.L.'],
      [config?.nit ? `NIT ${config.nit}` : ''],
      ['REPORTE DE INTERESES PAGADOS Y RETENCIONES ISR'],
      [`Del ${desde} al ${hasta}`],
      [''],
    ]
    const headers = ['Agencia', 'Tipo de Servicio', 'Cuenta', 'Código Asociado', 'Asociado',
      'Tipo de Movimiento', 'Descripción', 'Fecha', 'Crédito', 'Débito', 'Total Operación', 'Origen', 'Forma Pago']
    const detalle = rows.map(r => [
      '001 - NEOCOOP DIGITAL', r.servicio, r.numero_cuenta, r.no_asociado, r.asociado_nombre,
      r.tipo_movimiento, r.descripcion, r.fecha,
      Number(r.credito), Number(r.debito), Number(r.total), r.origen, r.forma_pago,
    ])
    const totalesRows = [
      [''],
      ['', '', '', '', '', '', 'TOTAL INTERESES', '', totales.intereses],
      ['', '', '', '', '', '', 'TOTAL ISR RETENIDO', '', '', totales.isr],
      ['', '', '', '', '', '', 'NETO CAPITALIZADO', '', '', '', totales.neto],
    ]
    const ws1 = XLSX.utils.aoa_to_sheet([...encabezado, headers, ...detalle, ...totalesRows])
    ws1['!cols'] = [18, 16, 16, 14, 34, 16, 38, 11, 10, 10, 12, 26, 20].map(w => ({ wch: w }))

    // Hoja 2 · Asociados únicos con NIT + DPI (para SAT).
    // NIT legacy si existe; si no, el CUI (desde marzo 2025 CUI = NIT
    // para personas individuales) marcado con asterisco.
    const unicos = new Map<string, Row>()
    rows.forEach(r => { if (!unicos.has(r.no_asociado)) unicos.set(r.no_asociado, r) })
    const ws2 = XLSX.utils.aoa_to_sheet([
      ['ASOCIADO', 'CÓDIGO', 'NIT', 'DPI', 'OBSERVACIÓN'],
      ...[...unicos.values()].map(r => [
        r.asociado_nombre, r.no_asociado, r.nit, r.dpi,
        r.nit_es_cui ? 'NIT = CUI (armonización SAT 2025)' : 'NIT registrado',
      ]),
    ])
    ws2['!cols'] = [{ wch: 40 }, { wch: 14 }, { wch: 16 }, { wch: 18 }, { wch: 32 }]

    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws1, 'Detalle')
    XLSX.utils.book_append_sheet(wb, ws2, 'Asociados DPI')
    XLSX.writeFile(wb, `reporte-intereses-isr-${desde}-a-${hasta}.xlsx`)
  }

  const onPDF = async () => {
    const config = await fetchConfigReporte()
    await exportarPDF({
      config,
      titulo: 'REPORTE DE INTERESES PAGADOS Y RETENCIONES ISR',
      subtitulo: `Del ${desde} al ${hasta}`,
      headers: ['Fecha', 'Servicio', 'Cuenta', 'Asociado', 'Tipo', 'Descripción', 'Crédito', 'Débito'],
      rows: rows.map(r => [
        r.fecha, r.servicio, r.numero_cuenta,
        `${r.asociado_nombre} (${r.no_asociado})`,
        r.tipo_movimiento === 'DESCUENTO ISR' ? 'ISR' : 'INTERÉS',
        r.descripcion,
        r.credito ? `Q ${fmt(r.credito)}` : '',
        r.debito ? `Q ${fmt(r.debito)}` : '',
      ]),
      totalRow: ['', '', '', '', '', 'TOTALES', `Q ${fmt(totales.intereses)}`, `Q ${fmt(totales.isr)}`],
      orientacion: 'landscape',
      columnasNumericas: [6, 7],
      filename: 'reporte-intereses-isr',
    })
  }

  return (
    <div style={{ padding: 20, maxWidth: 1400, margin: '0 auto' }}>
      <div style={{ marginBottom: 16 }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 500 }}>Intereses pagados y retenciones ISR</h1>
        <p style={{ margin: '4px 0 0', color: 'var(--muted-foreground)', fontSize: 13 }}>
          Detalle de capitalizaciones de interés y descuentos ISR por asociado.
          El Excel incluye una segunda hoja con DPI para gestiones SAT.
        </p>
      </div>

      {/* Filtros */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'auto 1fr 1fr 1fr 1.5fr auto auto auto auto',
        gap: 8, marginBottom: 12, padding: 12, alignItems: 'center',
        background: 'var(--surface, #fff)', border: '1px solid var(--border)', borderRadius: 8,
      }}>
        <Funnel size={16} color="var(--muted-foreground)" />
        <input type="date" value={desde} onChange={e => setDesde(e.target.value)} style={inp} />
        <input type="date" value={hasta} onChange={e => setHasta(e.target.value)} style={inp} />
        <select value={tipo} onChange={e => setTipo(e.target.value)} style={inp}>
          <option value="">Intereses + ISR</option>
          <option value="INTERES">Solo intereses</option>
          <option value="ISR">Solo ISR</option>
        </select>
        <input placeholder="Buscar asociado, código, descripción…" value={search}
          onChange={e => setSearch(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && cargar()} style={inp} />
        <button onClick={cargar} disabled={loading} style={btnPrimary}>
          {loading ? 'Cargando…' : 'Filtrar'}
        </button>
        <button onClick={onPDF} disabled={rows.length === 0} style={btnGhost} title="Exportar PDF">
          <FilePdf size={13} /> PDF
        </button>
        <button onClick={onExcel} disabled={rows.length === 0} style={btnGhost} title="Exportar Excel (2 hojas)">
          <MicrosoftExcelLogo size={13} /> Excel
        </button>
        <button onClick={() => window.print()} style={btnGhost}>
          <Printer size={13} /> Imprimir
        </button>
      </div>

      {/* Totales */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 16 }}>
        <Kpi label="Intereses pagados" value={`Q ${fmt(totales.intereses)}`} color="#047857" />
        <Kpi label="ISR retenido (10%)" value={`Q ${fmt(totales.isr)}`} color="#B45309" />
        <Kpi label="Neto capitalizado" value={`Q ${fmt(totales.neto)}`} color="var(--brand-600, #FF5B04)" />
      </div>

      {error && (
        <div style={{ padding: 10, background: '#fee2e2', color: '#991b1b', borderRadius: 6, fontSize: 13, marginBottom: 12 }}>
          {error}
        </div>
      )}

      {/* Tabla */}
      <div className="report-print-area" style={{ background: 'var(--surface, #fff)', border: '1px solid var(--border)', borderRadius: 8, overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead style={{ background: 'var(--n-50, #f4f4f5)', borderBottom: '1px solid var(--border)' }}>
            <tr>
              <th style={th}>Fecha</th>
              <th style={th}>Servicio</th>
              <th style={th}>Cuenta</th>
              <th style={th}>Asociado</th>
              <th style={th}>Tipo</th>
              <th style={th}>Descripción</th>
              <th style={{ ...th, textAlign: 'right' }}>Crédito</th>
              <th style={{ ...th, textAlign: 'right' }}>Débito</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && !loading && (
              <tr><td colSpan={8} style={{ padding: 24, textAlign: 'center', color: 'var(--muted-foreground)' }}>
                Sin movimientos de interés/ISR en el rango
              </td></tr>
            )}
            {rows.map((r, i) => (
              <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                <td style={td}>{r.fecha}</td>
                <td style={{ ...td, fontSize: 11 }}>{r.servicio}</td>
                <td style={{ ...td, fontFamily: 'monospace', fontSize: 11 }}>{r.numero_cuenta}</td>
                <td style={td}>
                  <div>{r.asociado_nombre}</div>
                  <div style={{ fontSize: 10, color: 'var(--muted-foreground)' }}>{r.no_asociado}</div>
                </td>
                <td style={td}>
                  <span style={{
                    display: 'inline-block', padding: '2px 8px', borderRadius: 10,
                    fontSize: 10, fontWeight: 500,
                    background: r.tipo_movimiento === 'DESCUENTO ISR' ? '#fef3c7' : '#dcfce7',
                    color: r.tipo_movimiento === 'DESCUENTO ISR' ? '#92400e' : '#166534',
                  }}>
                    {r.tipo_movimiento === 'DESCUENTO ISR' ? 'ISR' : 'Interés'}
                  </span>
                </td>
                <td style={{ ...td, maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {r.descripcion}
                </td>
                <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: '#047857' }}>
                  {Number(r.credito) > 0 ? `Q ${fmt(r.credito)}` : ''}
                </td>
                <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: '#B45309' }}>
                  {Number(r.debito) > 0 ? `Q ${fmt(r.debito)}` : ''}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: 8, fontSize: 12, color: 'var(--muted-foreground)', textAlign: 'right' }}>
        {rows.length} movimientos
      </div>
    </div>
  )
}

function Kpi({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <div style={{ padding: 14, background: 'var(--surface, #fff)', border: '1px solid var(--border)',
      borderRadius: 10, borderLeft: `3px solid ${color}` }}>
      <div style={{ fontSize: 11, color: 'var(--muted-foreground)', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 500 }}>
        {label}
      </div>
      <div style={{ fontSize: 20, fontWeight: 500, marginTop: 4, fontVariantNumeric: 'tabular-nums', color }}>{value}</div>
    </div>
  )
}

const inp: React.CSSProperties = {
  padding: '6px 10px', border: '1px solid var(--border)', borderRadius: 6,
  fontSize: 12, background: 'var(--surface, #fff)', fontFamily: 'inherit', boxSizing: 'border-box',
}
const btnPrimary: React.CSSProperties = {
  padding: '8px 16px', border: 'none', borderRadius: 6,
  background: 'var(--brand-600, #FF5B04)', color: '#fff',
  fontSize: 13, fontWeight: 500, cursor: 'pointer',
}
const btnGhost: React.CSSProperties = {
  padding: '6px 10px', border: '1px solid var(--border)', borderRadius: 6,
  background: 'var(--surface, #fff)', fontSize: 12, cursor: 'pointer',
  display: 'inline-flex', alignItems: 'center', gap: 4,
}
const th: React.CSSProperties = {
  padding: '8px 10px', textAlign: 'left', fontSize: 11,
  color: 'var(--muted-foreground)', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 500,
}
const td: React.CSSProperties = { padding: '8px 10px', verticalAlign: 'middle' }
